import { apiClient } from '../client'
import type {
  NotificationType,
  NotificationListResponse,
  NotificationUnreadCount,
  UserNotificationConfig,
  UpdateNotificationConfigRequest,
  NotificationEventType,
  NotificationRule,
  CreateRuleRequest,
  UpdateRuleRequest,
  RuleEvaluationLog,
} from '../../app/modules/types/notification'

export interface RegisterFCMTokenRequest {
  registration_id: string
  type: 'web' | 'android' | 'ios'
}

/**
 * Notifications API endpoints
 */
export const notificationsApi = {
  /**
   * Get notifications list (grouped)
   */
  getList: async (params?: string): Promise<NotificationListResponse> => {
    const url = params ? `/notification/?${params}` : '/notification/'
    const { data } = await apiClient.get(url)
    return data
  },

  /**
   * Mark a single notification as read
   */
  markAsRead: async (notificationId: string): Promise<{ success: boolean }> => {
    const { data } = await apiClient.post(`/notification/${notificationId}/mark_read/`)
    return data
  },

  /**
   * Get single notification detail
   */
  getDetail: async (notificationId: string): Promise<NotificationType> => {
    const { data } = await apiClient.get(`/notification/${notificationId}/`)
    return data
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async (): Promise<NotificationUnreadCount> => {
    const { data } = await apiClient.get('/notification/unread_count/')
    return data
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<{ success: boolean; marked_count: number }> => {
    const { data } = await apiClient.post('/notification/mark_all_read/')
    return data
  },

  /**
   * Mark a whole group as read by group_key
   */
  markGroupAsRead: async (groupKey: string): Promise<{ success: boolean; marked_count: number }> => {
    const { data } = await apiClient.post('/notification/mark_group_read/', { group_key: groupKey })
    return data
  },

  /**
   * Send a test notification to the current user
   */
  sendTestNotification: async (): Promise<{ success: boolean }> => {
    const { data } = await apiClient.post('/notification/test/')
    return data
  },

  /**
   * Register FCM token for push notifications
   */
  registerFCMToken: async (payload: RegisterFCMTokenRequest): Promise<void> => {
    await apiClient.post('/fcm/register/', payload)
  },

  /**
   * Unregister FCM token
   */
  unregisterFCMToken: async (token: string): Promise<void> => {
    await apiClient.delete(`/fcm/unregister/${token}/`)
  },
}

/**
 * Notification Settings V2 API — channel config, event types, rules
 */
export const notificationSettingsApi = {
  // ── Channel Config ──
  getConfig: async (): Promise<UserNotificationConfig> => {
    const { data } = await apiClient.get('/notification-config/')
    return data
  },

  updateConfig: async (payload: UpdateNotificationConfigRequest): Promise<UserNotificationConfig> => {
    const { data } = await apiClient.patch('/notification-config/update/', payload)
    return data
  },

  testChannel: async (channel: string): Promise<{ success: boolean }> => {
    const { data } = await apiClient.post('/notification-config/test-channel/', { channel })
    return data
  },

  // ── Event Types ──
  getEventTypes: async (): Promise<NotificationEventType[]> => {
    const { data } = await apiClient.get('/notification-event-types/')
    return data
  },

  // ── Rules ──
  getRules: async (): Promise<NotificationRule[]> => {
    const { data } = await apiClient.get('/notification-rules/')
    return data
  },

  createRule: async (payload: CreateRuleRequest): Promise<NotificationRule> => {
    const { data } = await apiClient.post('/notification-rules/', payload)
    return data
  },

  updateRule: async (id: string, payload: UpdateRuleRequest): Promise<NotificationRule> => {
    const { data } = await apiClient.patch(`/notification-rules/${id}/`, payload)
    return data
  },

  deleteRule: async (id: string): Promise<void> => {
    await apiClient.delete(`/notification-rules/${id}/`)
  },

  toggleRule: async (id: string): Promise<NotificationRule> => {
    const { data } = await apiClient.post(`/notification-rules/${id}/toggle/`)
    return data
  },

  getRuleLogs: async (ruleId: string): Promise<RuleEvaluationLog[]> => {
    const { data } = await apiClient.get(`/notification-rules/${ruleId}/logs/`)
    return data
  },
}
